import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

export default function Instalacao() {
    const { t } = useI18n();

    const benefits = [
        {
            title: t("Maior segurança operacional"),
            desc: t("Montagem certificada que cumpre as normas europeias de segurança para sistemas de armazenagem industrial."),
        },
        {
            title: t("Melhor aproveitamento do espaço"),
            desc: t("Configuração técnica otimizada para maximizar a capacidade útil do armazém, independentemente da dimensão."),
        },
        {
            title: t("Conformidade com normas técnicas"),
            desc: t("Instalação realizada em conformidade com as normas EN 15512 e SEMA, garantindo a sua tranquilidade."),
        },
        {
            title: t("Redução de erros e custos futuros"),
            desc: t("Uma instalação profissional evita retrabalhos, desgaste prematuro e custos imprevistos ao longo do tempo."),
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
            <Helmet>
                <title>{t("Serviço de Montagem e Instalação | Racksforall")}</title>
                <meta name="description" content={t("Serviço profissional de montagem e instalação de racks metálicos e estantes industriais. Rápido, seguro e em todo o Portugal.")} />
                <link rel="canonical" href="https://racksforall.pt/installation" />
                <meta property="og:title" content={t("Montagem e Instalação | Racksforall")} />
                <meta property="og:url" content="https://racksforall.pt/installation" />
            </Helmet>
            <div className="pt-20">
                {/* Hero */}
                <section className="relative overflow-hidden bg-indigo-600 text-white">
                    <div
                        className="absolute inset-0 opacity-[0.06]"
                        style={{
                            backgroundImage:
                                "repeating-linear-gradient(0deg, transparent, transparent 39px, white 39px, white 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, white 39px, white 40px)",
                        }}
                    />

                    <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none">
                        <svg
                            viewBox="0 0 1440 60"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-full h-16 fill-white"
                        >
                            <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" />
                        </svg>
                    </div>

                    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-24 lg:py-32 pb-28 text-center">
                        <span className="inline-block mb-5 rounded-full border border-white/30 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                            {t("Serviços")}
                        </span>

                        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-6">
                            {t("Instalação Profissional")}
                            <br />
                            <span className="text-indigo-200">{t("de Sistemas de Armazenamento")}</span>
                        </h1>

                        <p className="text-lg text-indigo-100 leading-relaxed max-w-2xl mx-auto mb-10">
                            {t("Da montagem à conformidade normativa — garantimos que o seu armazém fica operacional de forma rápida, segura e eficiente.")}
                        </p>

                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 rounded-xl bg-white text-indigo-600 px-6 py-3 text-sm font-semibold shadow hover:bg-indigo-50 transition"
                            >
                                {t("Pedir orçamento")}
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </Link>

                            <Link
                                to="/products"
                                className="inline-flex items-center gap-2 rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 transition"
                            >
                                {t("Ver produtos")}
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Sobre o serviço */}
                <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500 mb-3">
                                {t("O nosso serviço")}
                            </p>

                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-6">
                                {t("Um serviço completo, do projeto à entrega")}
                            </h2>

                            <p className="text-base text-gray-600 leading-relaxed mb-4">
                                {t("Na Racksforall, asseguramos um serviço completo de instalação de sistemas de armazenagem, adaptado às necessidades de cada projeto. Desde pequenas áreas comerciais até grandes armazéns industriais, garantimos uma montagem segura, eficiente e em conformidade com as normas europeias.")}
                            </p>

                            <p className="text-base text-gray-600 leading-relaxed">
                                {t("Trabalhamos com equipas técnicas qualificadas para garantir uma instalação rápida, minimizando interrupções na operação do cliente.")}
                            </p>


                        </div>

                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-100 bg-indigo-50">
                                <p className="text-sm font-semibold text-indigo-700">
                                    {t("Como funciona o processo")}
                                </p>
                            </div>

                            <ol className="divide-y divide-gray-100">
                                {[
                                    {
                                        step: "01",
                                        title: t("Avaliação do espaço"),
                                        desc: t("Visita técnica ou análise de planta para dimensionar a solução ideal."),
                                    },
                                    {
                                        step: "02",
                                        title: t("Proposta personalizada"),
                                        desc: t("Orçamento detalhado com especificações técnicas e prazo de execução."),
                                    },
                                    {
                                        step: "03",
                                        title: t("Entrega e montagem"),
                                        desc: t("Equipa especializada executa a instalação no prazo acordado."),
                                    },
                                    {
                                        step: "04",
                                        title: t("Verificação e entrega"),
                                        desc: t("Inspeção final de segurança e entrega da documentação técnica."),
                                    },
                                ].map((item) => (
                                    <li key={item.step} className="flex gap-5 px-6 py-5 items-start">
                                        <span className="shrink-0 w-8 h-8 rounded-lg bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                                            {item.step}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                                            <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                                                {item.desc}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </section>

                {/* Benefícios */}
                <section className="bg-white border-y border-gray-100">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
                        <div className="mb-12">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500 mb-3">
                                {t("Porquê escolher-nos")}
                            </p>
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {t("Benefícios da instalação profissional")}
                            </h2>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {benefits.map((b, i) => (
                                <div
                                    key={b.title}
                                    className="bg-white border border-gray-300 rounded-2xl px-8 py-8 group hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-xs font-bold text-indigo-400 tabular-nums group-hover:text-indigo-600 transition-colors">
                                            0{i + 1}
                                        </span>
                                        <span className="h-px flex-1 bg-gray-200" />
                                    </div>
                                    <h3 className="text-base font-semibold text-gray-900 mb-2">{b.title}</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20">
                    <div className="rounded-2xl bg-indigo-600 px-8 py-12 sm:px-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-2">{t("Pronto para começar?")}</h2>
                            <p className="text-indigo-200 text-sm max-w-md leading-relaxed">
                                {t("Fale connosco e receba uma proposta personalizada para a instalação do seu sistema de armazenagem.")}
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3 shrink-0">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 rounded-xl bg-white text-indigo-600 px-6 py-3 text-sm font-semibold hover:bg-indigo-50 transition whitespace-nowrap"
                            >
                                {t("Contactar agora")}
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </Link>

                            <Link
                                to="/products"
                                className="inline-flex items-center rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 transition whitespace-nowrap"
                            >
                                {t("Ver catálogo")}
                            </Link>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
}
